import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Save, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { ApplicationAccountModel } from '@/common/models/applicationAccount'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { ApplicationAccountForm } from '../schemas/ApplicationAccountSchema'

async function putEditApplicationAccount({
  id,
  ...data
}: ApplicationAccountModel) {
  return api.backend.put(`/application-account/${id}`, data)
}

export function useEditApplicationAccount() {
  const { t } = useTranslation('sso')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    ApplicationAccountModel
  >({
    mutationKey: ['/application-account/id'],
    mutationFn: putEditApplicationAccount,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Save,
          title: t('accounts.use_edit_application_account.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/account/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('accounts.use_edit_application_account.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('accounts.use_edit_application_account.toast_error.description'),
        },
      })
    },
  })

  const handleEditApplicationAccount = async (
    applicationAccount: ApplicationAccountModel,
    { accountFields, tag, ...data }: ApplicationAccountForm,
  ) => {
    const updatedApplicationAccount = {
      ...applicationAccount,
      ...data,
      accountFields: accountFields
        ? JSON.parse(accountFields)
        : applicationAccount.accountFields,
      tag: tag ? tag.map((t) => t.value) : applicationAccount.tag,
    }

    return mutateAsync(updatedApplicationAccount)
  }

  return {
    handleEditApplicationAccount,
    isEditingApplicationAccount: isPending,
  }
}
